import React, { useState } from 'react';
import { Combobox, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption } from '@headlessui/react';
import { ChevronDown, Check } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}

/**
 * Select com autocomplete (combobox). A lista completa ja vem carregada em
 * `options`; digitar filtra por rotulo. Usado nos campos que carregam listas
 * grandes (veiculo, motorista).
 */
const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  options,
  label,
  placeholder = 'Selecione...',
  required = false,
  disabled = false,
}) => {
  const [query, setQuery] = useState('');

  const filtered = query === ''
    ? options
    : options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()));

  const selected = options.find((o) => o.value === value) ?? null;

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <Combobox
        value={value}
        onChange={(v: string | null) => onChange(v ?? '')}
        disabled={disabled}
      >
        <div className="relative">
          <ComboboxInput
            className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            displayValue={() => selected?.label ?? ''}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            autoComplete="off"
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronDown className="h-4 w-4 text-gray-400" />
          </ComboboxButton>
          <ComboboxOptions className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">Nenhum resultado</div>
            ) : (
              filtered.map((opt) => (
                <ComboboxOption
                  key={opt.value}
                  value={opt.value}
                  className="group flex items-center justify-between px-3 py-2 text-sm cursor-pointer data-[focus]:bg-blue-50 data-[focus]:text-blue-700"
                >
                  <span>{opt.label}</span>
                  <Check className="h-4 w-4 text-blue-600 invisible group-data-[selected]:visible" />
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </div>
      </Combobox>
    </div>
  );
};

export default SearchableSelect;
